import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Workflow, RefreshCw, CheckCircle2, XCircle, Edit3, Eye,
  Clock, AlertTriangle, MailWarning, Filter as FilterIcon, ExternalLink,
} from 'lucide-react';

const FILTERS = [
  { key: 'pending',  label: 'Pending',  icon: Clock,         tone: 'amber' },
  { key: 'accepted', label: 'Accepted', icon: CheckCircle2,  tone: 'emerald' },
  { key: 'modified', label: 'Modified', icon: Edit3,         tone: 'blue' },
  { key: 'rejected', label: 'Rejected', icon: XCircle,       tone: 'rose' },
  { key: 'expired',  label: 'Expired',  icon: MailWarning,   tone: 'rose' },
  { key: 'all',      label: 'All',      icon: Eye,           tone: 'slate' },
];

const STATUS_TONE = {
  pending:  'bg-amber-500/15 text-amber-300 border-amber-500/30',
  accepted: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  modified: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  rejected: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  expired:  'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const ageLabel = (iso) => {
  if (!iso) return 'time unknown';
  try {
    const d = Date.now() - new Date(iso).getTime();
    if (d < 3600000) return `${Math.max(1, Math.round(d / 60000))}m old`;
    if (d < 86400000) return `${Math.max(1, Math.round(d / 3600000))}h old`;
    return `${Math.max(1, Math.round(d / 86400000))}d old`;
  } catch { return '—'; }
};

export default function ProtocolEvolutions() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(false);

  // Decision modals
  const [rejectModal, setRejectModal] = useState(null);   // {evo, reason}
  const [modifyModal, setModifyModal] = useState(null);   // {evo, reason, modified_change}
  const [busy, setBusy] = useState(null);                 // evo.id while a request runs

  const load = async (status = filter) => {
    setLoading(true);
    try {
      const [list, c] = await Promise.all([
        axios.get(`${API}/protocol-evolutions`, { params: { status }, withCredentials: true }),
        axios.get(`${API}/protocol-evolutions/counts`, { withCredentials: true }),
      ]);
      setItems(list.data?.evolutions || []);
      setCounts(c.data?.counts || {});
    } catch (e) {
      toast.error(`Failed to load: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const accept = async (evo) => {
    setBusy(evo.id);
    try {
      const r = await axios.post(`${API}/protocol-evolutions/${evo.id}/accept`, {}, { withCredentials: true });
      toast.success(`Accepted — review task ${r.data?.review_task_id} created.`);
      load(filter);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Accept failed'));
    } finally { setBusy(null); }
  };

  const submitReject = async () => {
    if (!rejectModal) return;
    const reason = (rejectModal.reason || '').trim();
    if (!reason) { toast.error('Reason is required.'); return; }
    setBusy(rejectModal.evo.id);
    try {
      await axios.post(`${API}/protocol-evolutions/${rejectModal.evo.id}/reject`, { reason }, { withCredentials: true });
      toast.success('Rejected.');
      setRejectModal(null);
      load(filter);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Reject failed'));
    } finally { setBusy(null); }
  };

  const submitModify = async () => {
    if (!modifyModal) return;
    const reason = (modifyModal.reason || '').trim();
    const modified_change = (modifyModal.modified_change || '').trim();
    if (!reason) { toast.error('Reason is required.'); return; }
    if (!modified_change) { toast.error('Modified change is required.'); return; }
    setBusy(modifyModal.evo.id);
    try {
      const r = await axios.post(
        `${API}/protocol-evolutions/${modifyModal.evo.id}/modify`,
        { reason, modified_change },
        { withCredentials: true },
      );
      toast.success(`Modified — review task ${r.data?.review_task_id} created.`);
      setModifyModal(null);
      load(filter);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Modify failed'));
    } finally { setBusy(null); }
  };

  const visible = useMemo(() => items, [items]);

  return (
    <AdminLayout>
      <div className="px-8 py-8 max-w-[1300px] mx-auto" data-testid="protocol-evolutions-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Workflow className="w-5 h-5 text-amber-400" />
              <h1 className="text-2xl font-medium text-white">Protocol Evolutions</h1>
              {!isAdmin && <Badge className="bg-slate-700 text-slate-300 text-xs">Read-only</Badge>}
            </div>
            <p className="text-sm text-slate-400 max-w-2xl">
              Suggested protocol evolutions detected from repeated accepted suggestions.
              Accepting <strong>does not</strong> mutate any active protocol — it creates a review task in
              Care Flow so an admin can apply the change through the existing guarded protocol publish flow.
            </p>
          </div>
          <Button
            data-testid="evolutions-refresh-btn"
            variant="outline"
            className="border-slate-700 text-slate-200 hover:bg-slate-800"
            onClick={() => load(filter)}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filter chips */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-6">
          {FILTERS.map(f => {
            const Icon = f.icon;
            const count = f.key === 'all' ? (counts.total || 0) : (counts[f.key] || 0);
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                data-testid={`evo-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`text-left rounded-lg border p-3 transition ${
                  active ? 'border-amber-500/40 bg-amber-500/5' : 'border-slate-800 hover:border-slate-700 bg-slate-900/40'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 mb-1 ${active ? 'text-amber-300' : 'text-slate-500'}`} />
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{f.label}</div>
                <div className="text-xl text-white">{count}</div>
              </button>
            );
          })}
        </div>

        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-slate-500">Loading…</div>
            ) : visible.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No protocol evolutions in this filter / scope.
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {visible.map(evo => (
                  <div
                    key={evo.id}
                    data-testid={`evo-row-${evo.status}`}
                    className="flex items-start gap-4 px-5 py-4"
                  >
                    <Badge className={`${STATUS_TONE[evo.status] || STATUS_TONE.expired} border text-[10px] uppercase tracking-wider whitespace-nowrap`}>
                      {evo.status}
                    </Badge>
                    <Badge
                      className="bg-slate-800/60 text-slate-300 border-0 text-[10px] whitespace-nowrap"
                      title={`Created: ${fmt(evo.created_at)}`}
                    >
                      {ageLabel(evo.created_at)}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white text-sm font-medium truncate">
                          {evo.subscriber_name || evo.subscriber_id}
                        </span>
                        {evo.subscriber_is_demo && (
                          <Badge className="bg-amber-500/15 text-amber-300 border-0 text-[8px] px-1.5">DEMO</Badge>
                        )}
                        {evo.action_type && (
                          <span className="text-xs text-slate-400 truncate">· {evo.action_type}</span>
                        )}
                        {evo.confidence && (
                          <Badge className="bg-slate-700/60 text-slate-300 border-0 text-[8px] px-1.5">
                            {evo.confidence} confidence
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-white/85 mb-1.5">
                        {evo.proposed_change || '—'}
                      </div>
                      {evo.modified_change && (
                        <div className="text-sm text-blue-200 mb-1.5">
                          <span className="text-blue-400 text-[10px] uppercase tracking-wider mr-1.5">Modified:</span>
                          {typeof evo.modified_change === 'string' ? evo.modified_change : JSON.stringify(evo.modified_change)}
                        </div>
                      )}
                      {evo.reason && (
                        <div className="text-[11px] text-slate-400 mb-1.5">
                          <span className="text-slate-500">evidence:</span> {evo.reason}
                        </div>
                      )}
                      {(evo.decision_reason || evo.decision_note || evo.expired_reason) && (
                        <div className="text-[11px] text-rose-200/80 mb-1.5">
                          <span className="text-slate-500">decision:</span>{' '}
                          {evo.decision_reason || evo.decision_note || evo.expired_reason}
                        </div>
                      )}
                      <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5">
                        <span>created: <code className="text-slate-300">{fmt(evo.created_at)}</code></span>
                        {evo.reviewed_at && <span>reviewed: <code className="text-slate-300">{fmt(evo.reviewed_at)}</code></span>}
                        {evo.expired_at && <span>expired: <code className="text-slate-300">{fmt(evo.expired_at)}</code></span>}
                        {typeof evo.evidence_count === 'number' && (
                          <span>evidence×<code className="text-slate-300">{evo.evidence_count}</code></span>
                        )}
                        {typeof evo.success_rate === 'number' && (
                          <span>success: <code className="text-slate-300">{evo.success_rate}%</code></span>
                        )}
                      </div>

                      {/* Read-only deep-link to the corresponding Care Flow review task,
                          if one was created on accept/modify. Admin-only since the
                          target page (Care Command Center) and the task itself are
                          admin-scoped. */}
                      {isAdmin && evo.review_task_id && (
                        <div className="mt-2.5">
                          <Link
                            to={`/admin/care-command-center?task_id=${encodeURIComponent(evo.review_task_id)}`}
                            data-testid={`evo-open-review-task-${evo.id}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
                                       border border-slate-700 bg-slate-900/60 text-[11px] text-slate-200
                                       hover:bg-slate-800 hover:border-slate-600 transition"
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span>Open review task</span>
                            <code className="text-slate-400 text-[10px]">{evo.review_task_id}</code>
                          </Link>
                        </div>
                      )}
                    </div>

                    {/* Admin-only action buttons (only on pending) */}
                    {isAdmin && evo.status === 'pending' && (
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button
                          data-testid={`evo-accept-${evo.id}`}
                          size="sm"
                          className="h-7 px-3 text-[10px] bg-emerald-700/40 text-emerald-100 hover:bg-emerald-700/60"
                          onClick={() => accept(evo)}
                          disabled={busy === evo.id}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                          Accept
                        </Button>
                        <Button
                          data-testid={`evo-modify-${evo.id}`}
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-[10px] border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
                          onClick={() => setModifyModal({ evo, reason: '', modified_change: evo.proposed_change || '' })}
                          disabled={busy === evo.id}
                        >
                          <Edit3 className="w-3.5 h-3.5 mr-1" />
                          Modify
                        </Button>
                        <Button
                          data-testid={`evo-reject-${evo.id}`}
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-[10px] border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                          onClick={() => setRejectModal({ evo, reason: '' })}
                          disabled={busy === evo.id}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reject modal */}
        {rejectModal && (
          <div
            data-testid="evo-reject-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setRejectModal(null); }}
          >
            <div className="w-full max-w-md rounded-lg border border-rose-500/30 bg-[#10141d] p-5">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-rose-300" />
                <h3 className="text-sm text-white/90">Reject protocol evolution</h3>
              </div>
              <p className="text-[11px] text-white/50 mb-4">
                Pending → <span className="text-rose-300">rejected</span>. A reason is required and will be written to the audit log.
              </p>
              <Textarea
                data-testid="evo-reject-reason"
                value={rejectModal.reason}
                onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
                rows={4}
                placeholder="Reason for rejecting (e.g. insufficient evidence for this subscriber, conflicting with current phase, side-effect risk)…"
                className="w-full bg-black/40 border-white/10 text-white/90 text-xs"
              />
              <div className="flex justify-end gap-2 mt-4">
                <Button size="sm" variant="outline"
                  className="h-7 px-3 text-[10px] border-white/10 text-white/70"
                  onClick={() => setRejectModal(null)}
                  data-testid="evo-reject-cancel"
                >Cancel</Button>
                <Button size="sm"
                  className="h-7 px-3 text-[10px] bg-rose-700/50 text-rose-100 hover:bg-rose-700/70"
                  onClick={submitReject}
                  disabled={busy === rejectModal.evo.id}
                  data-testid="evo-reject-submit"
                >Reject</Button>
              </div>
            </div>
          </div>
        )}

        {/* Modify modal */}
        {modifyModal && (
          <div
            data-testid="evo-modify-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setModifyModal(null); }}
          >
            <div className="w-full max-w-lg rounded-lg border border-blue-500/30 bg-[#10141d] p-5">
              <div className="flex items-center gap-2 mb-2">
                <Edit3 className="w-4 h-4 text-blue-300" />
                <h3 className="text-sm text-white/90">Modify & accept protocol evolution</h3>
              </div>
              <p className="text-[11px] text-white/50 mb-4">
                Pending → <span className="text-blue-300">modified</span>. A review task will be created in Care Flow with your modified payload.
                The active protocol is <strong>not</strong> changed automatically.
              </p>
              <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Modified change</label>
              <Textarea
                data-testid="evo-modify-change"
                value={modifyModal.modified_change}
                onChange={(e) => setModifyModal({ ...modifyModal, modified_change: e.target.value })}
                rows={3}
                placeholder="Edit the suggested change before accepting…"
                className="w-full bg-black/40 border-white/10 text-white/90 text-xs mb-3"
              />
              <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Reason</label>
              <Textarea
                data-testid="evo-modify-reason"
                value={modifyModal.reason}
                onChange={(e) => setModifyModal({ ...modifyModal, reason: e.target.value })}
                rows={3}
                placeholder="Why are you modifying this suggestion?"
                className="w-full bg-black/40 border-white/10 text-white/90 text-xs"
              />
              <div className="flex justify-end gap-2 mt-4">
                <Button size="sm" variant="outline"
                  className="h-7 px-3 text-[10px] border-white/10 text-white/70"
                  onClick={() => setModifyModal(null)}
                  data-testid="evo-modify-cancel"
                >Cancel</Button>
                <Button size="sm"
                  className="h-7 px-3 text-[10px] bg-blue-700/50 text-blue-100 hover:bg-blue-700/70"
                  onClick={submitModify}
                  disabled={busy === modifyModal.evo.id}
                  data-testid="evo-modify-submit"
                >Modify & Accept</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
